import { useCallback, useEffect, useState } from "react";
import { Download, RefreshCw, Search, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import {
  activeDocumentFilters, buildDocumentQuery, documentCategoryLabel, DOCUMENT_CATEGORIES,
  humanDocumentStatus,
} from "@/lib/adminDocuments";

const emptyFilters = { candidate: "", category: "", verification_status: "", uploaded_from: "", uploaded_to: "" };
const statuses = ["pending_scan", "clean", "infected", "scan_error"];
const formatDate = (value) => value ? new Date(value).toLocaleString("en-GB") : "Not recorded";

export default function AdminDocuments() {
  const [documents, setDocuments] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [state, setState] = useState("loading");
  const [message, setMessage] = useState("");

  const load = useCallback(async (selected) => {
    setState("loading");
    setMessage("");
    try {
      const { data } = await api.get("/admin/documents", { params: buildDocumentQuery(selected) });
      setDocuments(data.items || []);
      setState("ready");
    } catch (error) {
      const status = error?.response?.status;
      setDocuments([]);
      setState(status === 401 || status === 403 ? "unauthorized" : "error");
      setMessage(error?.response?.data?.detail || "Documents could not be loaded.");
    }
  }, []);

  useEffect(() => { load(emptyFilters); }, [load]);

  const search = () => {
    const selected = { ...filters };
    setAppliedFilters(selected);
    load(selected);
  };

  const clear = () => {
    const cleared = { ...emptyFilters };
    setFilters(cleared);
    setAppliedFilters(cleared);
    load(cleared);
  };

  const activeFilters = activeDocumentFilters(appliedFilters);

  const viewDocument = async (document) => {
    setMessage("");
    try {
      const response = await api.get(`/files/${document.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = window.document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) { setMessage(error?.response?.data?.detail || "This document cannot be opened."); }
  };

  const removeDocument = async (document) => {
    if (!window.confirm(`Withdraw ${document.document_name}?`)) return;
    try {
      await api.delete(`/files/${document.id}`);
      setDocuments((items) => items.filter((item) => item.id !== document.id));
      setMessage("Document withdrawn using the existing secure document workflow.");
    } catch (error) { setMessage(error?.response?.data?.detail || "This document cannot be withdrawn."); }
  };

  if (state === "unauthorized") return <section className="mt-8 card p-6" role="alert" data-testid="admin-documents-unauthorized"><h2 className="font-semibold">Documents</h2><p className="mt-2 text-sm text-amber-800">You do not have permission to view administrative document records.</p></section>;

  return (
    <section className="mt-8 card p-4 sm:p-6" data-testid="admin-documents">
      <div className="flex flex-wrap items-start justify-between gap-3"><div><h2 className="text-lg font-semibold">Documents</h2><p className="mt-1 text-sm text-slate-600">Private, scanned documents available to your tenant and role.</p></div><button type="button" className="btn btn-ghost" onClick={() => load(appliedFilters)} disabled={state === "loading"}><RefreshCw className="mr-2 h-4 w-4" />Refresh</button></div>

      <form className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" onSubmit={(event) => { event.preventDefault(); search(); }}>
        <label className="text-sm">Candidate<input className="field mt-1 w-full" value={filters.candidate} onChange={(event) => setFilters({ ...filters, candidate: event.target.value })} placeholder="Name or candidate ID" /></label>
        <label className="text-sm">Category<select className="field mt-1 w-full" value={filters.category} onChange={(event) => setFilters({ ...filters, category: event.target.value })}><option value="">All categories</option>{DOCUMENT_CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label className="text-sm">Verification status<select className="field mt-1 w-full" value={filters.verification_status} onChange={(event) => setFilters({ ...filters, verification_status: event.target.value })}><option value="">All statuses</option>{statuses.map((value) => <option key={value} value={value}>{humanDocumentStatus(value)}</option>)}</select></label>
        <label className="text-sm">Uploaded from<input type="date" className="field mt-1 w-full" value={filters.uploaded_from} onChange={(event) => setFilters({ ...filters, uploaded_from: event.target.value })} /></label>
        <label className="text-sm">Uploaded to<input type="date" className="field mt-1 w-full" value={filters.uploaded_to} onChange={(event) => setFilters({ ...filters, uploaded_to: event.target.value })} /></label>
        <div className="flex gap-2 sm:col-span-2 lg:col-span-5"><button type="submit" className="btn btn-primary"><Search className="mr-2 h-4 w-4" />Search</button><button type="button" className="btn btn-ghost" onClick={clear}>Clear</button></div>
      </form>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm" aria-live="polite">
        <span className="font-medium">Active filters:</span>
        {activeFilters.length === 0
          ? <span className="text-slate-600">None</span>
          : activeFilters.map((filter) => <span key={filter} className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">{filter}</span>)}
      </div>

      {message && <p className={`mt-4 text-sm ${state === "error" ? "text-red-700" : "text-slate-700"}`} role={state === "error" ? "alert" : "status"}>{message}</p>}
      {state === "loading" && <p className="mt-6 text-sm text-slate-600" role="status">Loading documents…</p>}
      {state === "ready" && <p className="mt-4 text-sm text-slate-600" role="status">{documents.length} {documents.length === 1 ? "document" : "documents"} found</p>}
      {state === "ready" && documents.length === 0 && <p className="mt-3 rounded-lg bg-slate-50 p-4 text-sm text-slate-600">{activeFilters.length ? "No documents match the active filters." : "No documents are available."}</p>}
      {state === "ready" && documents.length > 0 && <div className="mt-6 space-y-3">{documents.map((document) => <article key={document.id} className="rounded-xl border border-slate-200 p-4"><div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-[1.2fr_1.4fr_1fr_1fr_1fr_auto]"><div><span className="text-xs uppercase text-slate-500">Candidate</span><p className="font-medium">{document.candidate_name}</p><p className="break-all text-xs text-slate-500">{document.candidate_id || "Not recorded"}</p></div><div><span className="text-xs uppercase text-slate-500">Document</span><p className="break-words font-medium">{document.document_name}</p><p className="text-xs text-slate-500">{documentCategoryLabel(document.category)}</p></div><div><span className="text-xs uppercase text-slate-500">Status</span><p>{humanDocumentStatus(document.verification_status)}</p></div><div><span className="text-xs uppercase text-slate-500">Uploaded</span><p>{formatDate(document.uploaded_at)}</p></div><div><span className="text-xs uppercase text-slate-500">Updated</span><p>{formatDate(document.updated_at)}</p></div><div className="flex flex-wrap items-center gap-2 lg:justify-end">{document.can_view && <button type="button" className="btn btn-ghost" onClick={() => viewDocument(document)}><Download className="mr-1 h-4 w-4" />View</button>}{document.can_delete && <button type="button" className="btn btn-ghost text-red-700" onClick={() => removeDocument(document)}><Trash2 className="mr-1 h-4 w-4" />Withdraw</button>}{!document.can_view && !document.can_delete && <span className="text-xs text-slate-500">No actions available</span>}</div></div></article>)}</div>}
    </section>
  );
}
